import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export interface TurnEntry {
  kind: "user" | "agent_text" | "tool";
  text?: string;
  toolName?: string;
  toolArgs?: unknown;
  toolDurationMs?: number;
  toolError?: boolean;
}

function formatArgs(args: unknown): string {
  if (!args || typeof args !== "object") return "";
  return Object.entries(args as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => {
      const vs = typeof v === "string" ? v : JSON.stringify(v);
      return `${k}: ${vs.length > 40 ? vs.slice(0, 40) + "…" : vs}`;
    })
    .join(", ");
}

function AgentEventViewImpl({ entry }: { entry: TurnEntry }) {
  if (entry.kind === "user") {
    return (
      <div className="text-block user">
        <strong>you › </strong>
        {entry.text}
      </div>
    );
  }
  if (entry.kind === "agent_text") {
    return (
      <div className="text-block markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {entry.text ?? ""}
        </ReactMarkdown>
      </div>
    );
  }
  if (entry.kind === "tool") {
    const args = formatArgs(entry.toolArgs);
    const status =
      entry.toolDurationMs == null ? "●" : entry.toolError ? "✗" : "✓";
    const cls =
      entry.toolError
        ? "tool-end-err"
        : entry.toolDurationMs != null
          ? "tool-end-ok"
          : "tool-call";
    return (
      <div className={cls}>
        {status} {entry.toolName}
        {args && ` (${args})`}
        {entry.toolDurationMs != null && ` — ${entry.toolDurationMs}ms`}
      </div>
    );
  }
  return null;
}

// Memoized: ChatView re-renders on every keystroke (input state changes), but
// most entries are referentially stable across those re-renders. Without memo,
// every keystroke re-parses every markdown block via react-markdown, which
// dominates render cost once the scrollback grows. Default shallow prop
// equality is correct here — TurnEntry objects are created fresh only when
// their content actually changes (see ChatView's setEntries map).
export const AgentEventView = memo(AgentEventViewImpl);
